import cx from "classnames";
import { debounce } from "lodash";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  useImperativeHandle,
} from "react";

import PlayButton from "components/VideoPlayer/PlayButton";
import VideoSlider from "components/VideoPlayer/Slider";
import "polyfill";
import { DEFAULT_FPS, WINDOW_EVENTS } from "constants/constants";
import { hexToRGB } from "utils/helper";

import styles from "./VideoPlayer.module.scss";

interface VideoPlayerProps {
  videoURL: string;
  totalFrames: number;
  firstFrame: number;
  onPlay?: () => void;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  fps: number;
  startTime: number;
  videoWidth?: number;
  videoHeight?: number;
  color?: string;
  loop?: boolean;
  isForReview?: boolean;
  /** Scale preview to fill the review area (e.g. Smart Vectors) */
  fillPreview?: boolean;
}

export interface VideoPlayerRefAttributes {
  pause: () => void;
  seekToFrame: (frame: number) => void;
  onPlay: () => void;
  setImageDatas: (datas: ImageBitmap[]) => void;
  setWidthHeight: (wh: [number, number]) => void;
  imageDatas: ImageBitmap[];
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  currentFrame: number;
}

const VideoPlayer = ({
  videoURL,
  totalFrames,
  firstFrame,
  onPlay,
  setIsLoading,
  fps,
  startTime,
  color,
  videoWidth,
  videoHeight,
  loop = true,
  isForReview = false,
  fillPreview = false,
  ref,
}: VideoPlayerProps & { ref?: React.Ref<VideoPlayerRefAttributes> }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const baseCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [markStyles, setMarkStyles] = useState({});
  const [widthHeight, setWidthHeight] = useState<[number, number]>([0, 0]);
  const [imageDatas, setImageDatas] = useState<ImageBitmap[]>([]);
  const [marks, setMarks] = useState<{ value: number; label: string }[]>([]);
  const [showControls, setShowControls] = useState(false);
  const frameCallbackRef = useRef<number[]>([]);

  const scaleRef = useRef(1);
  const positionRef = useRef({ x: 0, y: 0 });

  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const calculatedFps = useMemo(() => {
    if (fps) {
      return fps > 999 ? DEFAULT_FPS : fps;
    }

    try {
      const video = videoRef.current;
      if (
        video &&
        video.duration &&
        !Number.isNaN(video.duration) &&
        totalFrames > 0
      ) {
        const calculatedValue = Math.round(totalFrames / video.duration);
        return calculatedValue > 0 && calculatedValue <= 999
          ? calculatedValue
          : DEFAULT_FPS;
      }
    } catch (error) {
      console.warn("Error calculating fps from video duration:", error);
    }

    return DEFAULT_FPS;
  }, [fps, totalFrames]);

  const _getFps = useCallback(() => {
    return calculatedFps;
  }, [calculatedFps]);

  useEffect(() => {
    if (!baseCanvasRef.current) {
      baseCanvasRef.current = document.createElement("canvas");
    }
    if (!overlayCanvasRef.current) {
      overlayCanvasRef.current = document.createElement("canvas");
    }

    return () => {
      baseCanvasRef.current = null;
      overlayCanvasRef.current = null;
    };
  }, []);

  const pause = () => {
    if (videoRef.current) {
      setIsPlaying(false);
      videoRef.current.pause();
    }
  };

  const play = () => {
    if (videoRef.current) {
      setIsPlaying(true);
      videoRef.current.play();
    }
  };

  const seekToFrame = useCallback(
    (frame: number) => {
      pause();
      setCurrentFrame(frame);
      const eVideo = videoRef.current;
      if (eVideo && eVideo.duration && totalFrames) {
        eVideo.currentTime = frame / _getFps() + 0.0005;
      }
    },
    [_getFps, totalFrames],
  );

  const sliderChange = debounce((value) => {
    seekToFrame(+value);
  }, 500);

  const handleKeyDown = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT") {
      return;
    }
    switch (e.key) {
      case "ArrowRight":
        pause();
        seekToFrame(Math.min(currentFrame + 1, totalFrames - 1));
        break;
      case "ArrowLeft":
        pause();
        seekToFrame(Math.max(currentFrame - 1, 0));
        break;
      case "k":
      case " ":
        if (isPlaying) {
          pause();
        } else {
          play();
        }
        break;
      // no default
    }
  };

  const render = useCallback(
    (datas: ImageBitmap[], cf = currentFrame) => {
      const video = videoRef.current;
      if (!video) return;

      const w = videoWidth || video.videoWidth || widthHeight[0];
      const h = videoHeight || video.videoHeight || widthHeight[1];

      if (!baseCanvasRef.current) {
        baseCanvasRef.current = document.createElement("canvas");
      }
      const baseCanvas = baseCanvasRef.current;
      if (!baseCanvas) return;

      if (baseCanvas.width !== w || baseCanvas.height !== h) {
        baseCanvas.width = w;
        baseCanvas.height = h;
      }
      const baseCtx = baseCanvas.getContext("2d");
      if (!baseCtx) return;

      baseCtx.drawImage(video, 0, 0, w, h);

      if (datas[cf]) {
        if (!overlayCanvasRef.current) {
          overlayCanvasRef.current = document.createElement("canvas");
        }
        const overlayCanvas = overlayCanvasRef.current;
        if (!overlayCanvas) return;

        if (overlayCanvas.width !== w || overlayCanvas.height !== h) {
          overlayCanvas.width = w;
          overlayCanvas.height = h;
        }
        const overlayCtx = overlayCanvas.getContext("2d");
        if (!overlayCtx) return;

        overlayCtx.drawImage(datas[cf], 0, 0, w, h);

        const baseImageData = baseCtx.getImageData(0, 0, w, h);
        const overlayImageData = overlayCtx.getImageData(0, 0, w, h);

        const { r, g, b } = hexToRGB(color || "#FF0000");

        for (let i = 0; i < baseImageData.data.length; i += 4) {
          if (
            overlayImageData.data[i] >= 128 &&
            overlayImageData.data[i + 1] === 0 &&
            overlayImageData.data[i + 2] === 0 &&
            overlayImageData.data[i + 3] === 255
          ) {
            baseImageData.data[i] = baseImageData.data[i] * 0.5 + r * 0.8;
            baseImageData.data[i + 1] =
              baseImageData.data[i + 1] * 0.5 + g * 0.8;
            baseImageData.data[i + 2] =
              baseImageData.data[i + 2] * 0.5 + b * 0.8;
            baseImageData.data[i + 3] = 255;
          }
        }

        baseCtx.putImageData(baseImageData, 0, 0);
      }

      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (isForReview) {
        const scale = scaleRef.current;
        const position = positionRef.current;

        ctx.save();
        ctx.translate(position.x, position.y);
        ctx.scale(scale, scale);
        ctx.drawImage(baseCanvas, 0, 0, w, h);
        ctx.restore();
      } else {
        ctx.drawImage(baseCanvas, 0, 0, w, h);
      }
    },
    [color, currentFrame, videoHeight, videoWidth, widthHeight, isForReview],
  );

  // Default behavior for onLoadedData event
  const handleOnPlay = useCallback(() => {
    if (!videoRef.current) {
      return;
    }
    const step = (now, metadata) => {
      if (!videoRef.current) {
        return;
      }
      const mediaTime =
        videoRef.current.currentTime < startTime ? 0 : metadata.mediaTime;
      const cf = Math.round(_getFps() * mediaTime);
      setImageDatas((datas) => {
        render(datas, cf);
        return datas;
      });
      setCurrentFrame(cf);
      frameCallbackRef.current.forEach((id) =>
        videoRef.current?.cancelVideoFrameCallback(id),
      );
      frameCallbackRef.current = [
        videoRef.current?.requestVideoFrameCallback(step),
      ];
    };
    frameCallbackRef.current.forEach((id) =>
      videoRef.current?.cancelVideoFrameCallback(id),
    );
    frameCallbackRef.current = [
      videoRef.current?.requestVideoFrameCallback(step),
    ];
  }, [_getFps, render, startTime]);

  useImperativeHandle(ref, () => ({
    pause() {
      pause();
    },
    seekToFrame(frame) {
      seekToFrame(frame);
    },
    onPlay() {
      if (onPlay) onPlay();
      else handleOnPlay();
    },
    setImageDatas(datas) {
      const next = new Set(datas);
      imageDatas.forEach((bitmap) => {
        if (bitmap && !next.has(bitmap) && typeof bitmap.close === "function") {
          bitmap.close();
        }
      });
      setImageDatas([...datas]);
      render(datas);
    },
    setWidthHeight(wh) {
      setWidthHeight(wh);
    },
    videoRef,
    canvasRef,
    currentFrame,
    imageDatas,
  }));

  useEffect(() => {
    const segments = 20;
    const desiredMultiple = 5;
    const mrks: { value: number; label: string }[] = [];
    const splitValue =
      totalFrames < desiredMultiple
        ? 1
        : Math.max(
            1,
            Math.ceil(+(totalFrames / segments).toFixed() / desiredMultiple),
          ) * desiredMultiple;
    for (let i = 1; i <= totalFrames; i++) {
      mrks.push({
        value: i + firstFrame - 1,
        label: i % splitValue === 0 || i === 1 ? `${i + firstFrame - 1}` : "",
      });
    }
    setMarks(mrks);
    const style = {};
    for (let i = 1; i <= totalFrames / splitValue; i++) {
      style[`&[data-index="${i * splitValue - 1}"]`] = {
        backgroundColor: "#0B0B0F",
      };
    }
    setMarkStyles(style);
  }, [firstFrame, totalFrames]);

  useEffect(() => {
    const videoEle = videoRef.current;
    if (!videoEle) return;
    if (videoURL && videoEle.src !== videoURL) {
      setShowControls(false);
      if (setIsLoading) setIsLoading(true);
      videoEle.src = videoURL;
      videoEle.load();
    }
  }, [setIsLoading, videoURL]);

  const handleCanvasWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    if (!isForReview || !canvasRef.current) return;

    e.preventDefault();

    const scale = scaleRef.current;
    const position = positionRef.current;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const mouseX = e.clientX - rect.left + 380;
    const mouseY = e.clientY - rect.top + 200;

    const imageX = (mouseX - position.x) / scale;
    const imageY = (mouseY - position.y) / scale;

    const scaleBy = 1.05;
    const direction = e.deltaY < 0 ? 1 : -1;
    const newScale = Math.min(
      5,
      direction > 0 ? scale * scaleBy : scale / scaleBy,
    );

    if (newScale <= 1) {
      scaleRef.current = 1;
      positionRef.current = { x: 0, y: 0 };
      render(imageDatas, currentFrame);
      return;
    }

    const newPos = {
      x: mouseX - imageX * newScale,
      y: mouseY - imageY * newScale,
    };

    scaleRef.current = newScale;
    positionRef.current = newPos;
    render(imageDatas, currentFrame);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isForReview) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isForReview || !isDragging || !canvasRef.current) return;

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;

    dragStartRef.current = { x: e.clientX, y: e.clientY };

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const scale = scaleRef.current;
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const imageWidth = (videoWidth || widthHeight[0]) * scale;
    const imageHeight = (videoHeight || widthHeight[1]) * scale;

    const minX = Math.min(0, canvasWidth - imageWidth);
    const minY = Math.min(0, canvasHeight - imageHeight);

    let newX = positionRef.current.x + dx;
    let newY = positionRef.current.y + dy;

    newX = Math.max(minX, Math.min(0, newX));
    newY = Math.max(minY, Math.min(0, newY));

    positionRef.current = { x: newX, y: newY };

    render(imageDatas, currentFrame);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const handleGetCurrentFrame = () => {
      window.dispatchEvent(
        new CustomEvent(WINDOW_EVENTS.CURRENT_FRAME_REPLY, {
          detail: {
            frame: currentFrame,
          },
        }),
      );
    };

    window.addEventListener(
      WINDOW_EVENTS.GET_CURRENT_FRAME,
      handleGetCurrentFrame,
    );
    return () => {
      window.removeEventListener(
        WINDOW_EVENTS.GET_CURRENT_FRAME,
        handleGetCurrentFrame,
      );
    };
  }, [currentFrame]);

  return (
    <div
      className={cx(
        styles.mainContainer,
        fillPreview && styles.mainContainerFill,
      )}
    >
      <div
        className={cx(
          styles.canvasContainer,
          fillPreview && styles.canvasContainerFill,
        )}
      >
        <canvas
          className={cx(
            styles.previewImage,
            fillPreview && styles.previewImageFill,
          )}
          id="canvas"
          ref={canvasRef}
          width={videoWidth || videoRef.current?.videoWidth || widthHeight[0]}
          height={
            videoHeight || videoRef.current?.videoHeight || widthHeight[1]
          }
          style={{
            visibility: showControls ? "visible" : "hidden",
            cursor: isDragging ? "grab" : "default",
          }}
          onWheel={isForReview ? handleCanvasWheel : undefined}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
        <video
          ref={videoRef}
          muted
          crossOrigin="anonymous"
          preload="auto"
          onEnded={() => {
            if (loop) {
              play();
            } else {
              pause();
            }
          }}
          onLoadedMetadata={() => {
            handleOnPlay();
            if (onPlay) onPlay();
            if (setIsLoading) setIsLoading(false);
            setShowControls(true);
          }}
          style={{
            visibility: "hidden",
            width: "1px",
            height: "1px",
            position: "absolute",
          }}
          controls
        />
      </div>
      {showControls && (
        <div className={styles.playerControls}>
          <div className={styles.sliderPlayBtnCon}>
            <div className={styles.playBtn}>
              <PlayButton
                onPause={pause}
                onPlay={play}
                disabled={false}
                isPlaying={isPlaying}
              />
            </div>

            <div className={styles.sliderCon}>
              <VideoSlider
                onChange={(e, value) => {
                  if (isPlaying) {
                    seekToFrame(+value - firstFrame);
                  } else {
                    sliderChange(+value - firstFrame);
                  }
                }}
                handleKeyDown={handleKeyDown}
                totalFrames={totalFrames}
                currentFrame={currentFrame + firstFrame}
                firstFrame={firstFrame}
                marks={marks}
                markStyles={markStyles}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
